import React, {PropsWithChildren} from 'react';
import {concepts} from "fearless-common";
import Nav from "react-bootstrap/Nav";
import Button from "react-bootstrap/Button";
import {CloudArrowDownFill} from "react-bootstrap-icons";
import {routes} from "../utils";
import useAuth from "./hooks/useAuth";
import trackEvent, {ACTIONS, CATEGORIES} from "./tracking/trackEvent";

type Props = PropsWithChildren<{
    /** Extra class names applied to the rendered element. */
    className?: string;
    /** Element to render as. Defaults to a React Bootstrap `Button`; pass
     *  `Nav.Link` for navbar usage or `'a'` for a plain anchor. */
    component?: typeof Nav.Link | typeof Button | 'a';
    /** Analytics action sent to `trackEvent` on click. When omitted, clicks
     *  are not tracked. */
    actionTag?: ACTIONS;
    /** Arbitrary props spread onto the rendered component. */
    componentProps?: Record<string, any>;
    /** Label shown to anonymous (logged-out) visitors. Authenticated users
     *  always see the "Download Photo51" label instead. */
    text: string;
}>;

/**
 * Context-aware call-to-action button.
 *
 * - Anonymous visitor → links to the sign-up page and shows `text`.
 * - Authenticated user → links to the Photo51 download page and shows
 *   "Download Photo51".
 *
 * Both `to` (router-style) and `href` (anchor-style) point at the same URL so
 * the button works whether rendered as a `Link`-like component or a plain
 * anchor. When `actionTag` is provided, a `SIGN_UP_CTA` analytics event is
 * fired on click with the resolved label.
 *
 * Custom `children` override the default icon + label content.
 */
export default function SignUpButton({
    className = '',
    component: Component = Button,
    componentProps = {},
    actionTag,
    children,
    text,
}: Props) {
    const { user } = useAuth()
    const url = user ? routes.download : routes.signUp;
    const label = user ? 'Download '+concepts.Photo51 : text;

    return (
        <Component
            {...componentProps}
            className={className}
            // @ts-ignore
            to={url}
            href={url}
            onClick={() => {
                if (actionTag) trackEvent(CATEGORIES.SIGN_UP_CTA, actionTag, label);
            }}
        >
            {children || (
                <>
                    <CloudArrowDownFill size={30} />
                    &nbsp;&nbsp;{label}
                </>
            )}
        </Component>
    );
}
